<script setup lang="ts">
import type { RouteRecordName } from 'vue-router';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import TransitionFade from '@/components/transitions/TransitionFade.vue';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { MixpanelInstance } from '@/plugins/mixpanel/mixpanel';
import { useLocaleStore } from '@/stores/locales';
import Footer from '@/views/side-panels/share-profile/Footer.vue';
import ShareLink from '@/views/side-panels/share-profile/ShareLink.vue';
import ShareSynthesis from '@/views/side-panels/share-profile/ShareSynthesis.vue';

const router = useRouter();
const route = useRoute();
const isDesktop = useMediaQuery('(min-width: 764px)');
const { auth } = useMeQuery();

const panelRef = ref<HTMLElement | null>(null);
const currentTab = ref((route.query.share as 'link' | 'synthesis') || 'link');

const TABS = [
  {
    name: 'link',
    component: ShareLink,
    icon: 'fat fa-share-nodes',
  },
  {
    name: 'synthesis',
    component: ShareSynthesis,
    icon: 'fat fa-arrow-down-to-line',
  },
] as const;

const currentComponent = computed(
  () => TABS.find((tab) => tab.name === currentTab.value)?.component,
);
const navGap = computed(() => (isDesktop.value ? '16px' : '0'));

function goBack() {
  router.push({ name: route.name as RouteRecordName });
}

function handleRouting(value: 'link' | 'synthesis') {
  currentTab.value = value;
  router.push({ name: route.name as RouteRecordName, query: { share: currentTab.value } });
}

// Mixpanel
onMounted(() => {
  if (!import.meta.env.DEV) {
    MixpanelInstance.track('side-panel_viewed', {
      name: 'share_profile',
      user_uuid: auth.value?.uuid,
      email: auth.value?.email,
      slug: route.fullPath,
      domain: window.location.hostname,
    });
  }
});

/**
 * @todo use html dialog element
 */
onMounted(() => {
  const app = document.querySelector('#app') as HTMLElement;
  app.inert = true;
  document.documentElement.style.overflow = 'hidden';
});

onUnmounted(() => {
  const app = document.querySelector('#app') as HTMLElement;
  app.inert = false;
  document.documentElement.style.overflow = '';
});

await useLocaleStore().loadNamespaces('shareProfile');
</script>

<template>
  <div class="h-full fixed inset-0 z-2000 bg-black/25 grid backdrop-blur-xs" @click.self="goBack">
    <div
      class="bg-white w-full h-full flex flex-col md:justify-self-end md:w-2/3 lg:w-1/2 text-af-grey-600 inner"
      ref="panelRef"
    >
      <header
        class="h-16 flex items-center justify-between gap-2 md:gap-6 border-b px-2 md:px-5! py-4"
      >
        <h1
          class="font-heading font-extrabold text-base md:text-xl leading-none flex items-center gap-2 text-af-grey-700 text-nowrap"
        >
          <span class="rounded-full w-5 h-5 md:w-8 md:h-8 grid place-items-center bg-teal">
            <!-- -ms-0.5 is a fix for the icon position -->
            <VIcon icon="fas fa-share-nodes" class="-ms-0.5 text-sm! md:text-lg!" />
          </span>
          {{ $t('candidate_account.share.panel.title') }}
        </h1>
        <button type="button" @click="goBack" class="grid place-items-center grow-0">
          <VIcon icon="fat fa-times" class="text-sm md:text-xl" />
        </button>
      </header>

      <div
        class="max-h-[calc(100dvh-4rem)] flex-1 flex flex-col md:grid md:grid-cols-[max-content_1fr] md:grid-rows-1"
      >
        <nav class="bg-[#f9fafb] shadow-[inset_3px_0px_0_0_#ffeaf4] shrink-0">
          <VTabs
            :direction="isDesktop ? 'vertical' : 'horizontal'"
            grow
            :model-value="currentTab"
            class="h-[unset]"
          >
            <VTab
              v-for="tab in TABS"
              :key="tab.name"
              :value="tab.name"
              :prepend-icon="tab.icon"
              :tabindex="0"
              @click="handleRouting(tab.name)"
              class="py-[6px] px-2 md:px-4 uppercase text-af-grey-700 min-h-14 font-regular text-xs flex gap-2 flex-col items-center md:min-h-11 md:justify-start md:flex-row"
            >
              {{ $t(`candidate_account.share.panel.tab_${tab.name === 'link' ? 1 : 2}`) }}
            </VTab>
          </VTabs>
        </nav>

        <div class="flex-1 overflow-y-auto">
          <TransitionFade>
            <component :is="currentComponent" />
          </TransitionFade>
        </div>

        <Footer class="md:col-span-2" />
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.v-btn__prepend) {
  margin: 0;
}

:deep(.v-slide-group__content) {
  gap: v-bind(navGap);
}
</style>
